<main>
  <loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>

  <header>
    <div class="headline">
      <h1>
        {{
          isEditMode()
            ? ('Edit_Recipe' | translateWF: 'Edit Recipe')
            : ('Create_Recipe'
              | translateWF
                : 'Create
        Recipe')
        }}
      </h1>
    </div>
    <div class="buttons">
      @if (canEdit()) {
        <p-button [label]="'Common_Save' | translateWF: 'Save'" [disabled]="formGroup.invalid" (click)="onSave()" />
      }
      <p-button [label]="'Common_Cancel' | translateWF: 'Cancel'" variant="outlined" (click)="onCancel()" />
    </div>
  </header>

  <div class="content">
    <form [formGroup]="formGroup">
      <section>
        <div class="grid-12">
          <!-- Lab Dip Reference -->
          <div class="span-3">
            <div class="form-box">
              <label for="labDipReference" class="required">
                {{ 'Lab_Dip' | translateWF: 'Lab Dip' }}
              </label>
              <p-autoComplete
                class="w-full"
                id="labDipReference"
                formControlName="labDipReference"
                #labDipSearch="cslServerSearch"
                [cslServerSearch]="fetchLabDips"
                [suggestions]="labDipSearch.results()"
                [placeholder]="'Search_Lab_Dip' | translateWF: 'Search Lab Dip'"
                [dropdown]="true"
                appendTo="body"
                field="labDipNumber"
                styleClass="w-full"
              >
                <ng-template let-labDip pTemplate="item">
                  <div class="col">
                    <span class="font-semibold">{{ labDip.labDipNumber }}</span>
                    @if (labDip.name) {
                      <span class="text-sm text-gray-600">{{ labDip.name }}</span>
                    }
                  </div>
                </ng-template>
              </p-autoComplete>
            </div>
          </div>

          <!-- Recipe Number -->
          <div class="span-3">
            <div class="form-box">
              <label for="recipeNumber">
                {{ 'Recipe_Number' | translateWF: 'Recipe Number' }}
              </label>
              <input id="recipeNumber" type="text" pInputText formControlName="recipeNumber" [readonly]="true" class="w-full p-disabled" />
            </div>
          </div>

          <!-- Liquor Ratio -->
          <div class="span-3">
            <div class="form-box">
              <label for="liquorRatio" class="required">
                {{ 'Liquor_Ratio' | translateWF: 'Liquor Ratio' }}
              </label>
              <p-inputNumber
                class="w-full"
                id="liquorRatio"
                formControlName="liquorRatio"
                [minFractionDigits]="1"
                [maxFractionDigits]="2"
                [min]="0.1"
                [max]="100"
                [placeholder]="'Enter_Liquor_Ratio' | translateWF: 'Enter Liquor Ratio'"
                styleClass="w-full"
              />
            </div>
          </div>

          <!-- Shade Reference -->
          <div class="span-3">
            <div class="form-box">
              <label for="shade" class="required">
                {{ 'Shade' | translateWF: 'Shade' }}
              </label>
              <p-select
                class="w-full"
                id="shade"
                formControlName="shadeReference"
                [options]="shadeOptions()"
                [placeholder]="'Select_Shade' | translateWF: 'Select a shade'"
                appendTo="body"
                styleClass="w-full"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Notes Section -->
      @if (userLite()) {
        <section>
          <csl-standard-notes [notes]="notes()" [userLite]="userLite()!" [viewOnly]="!canEdit()" (noteAdded)="onNoteAdded()" (viewMore)="onViewMore()" />
        </section>
      }

      <app-process-details-viewer
        [formControl]="processes"
        [canEdit]="canEdit()"
        [canEditParameters]="canEdit()"
        [canEditItems]="canEdit()"
        [canEditSteps]="canEdit()"
        [inventoryTypeReference]="'6866c33a245e3504d98d3a44'"
        [processTypeReference]="'67aab7b56b1dfb85a9311f21'"
      />
    </form>

    <!-- Manufacturer Lot Numbers Section -->
    <section>
      <div class="page__title">
        <div class="page__title_container">
          <h3>Manufacturer Lot Numbers</h3>
          <div class="page__title_buttons">
            @if (canEdit()) {
              <p-button label="Add" icon="pi pi-plus" (onClick)="addManufacturerLotNumber()" />
            }
          </div>
        </div>
      </div>

      <p-table [value]="manufacturerLotNumbers.controls" [tableStyle]="{ 'min-width': '50rem' }">
        <ng-template pTemplate="header">
          <tr>
            <th>Manufacturer</th>
            <th>Manufacturer Lot Number</th>
            <th>Date Added</th>
            <th>Added By</th>
            <th style="width: 5rem">Actions</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-row let-i="rowIndex">
          <tr [formGroup]="row">
            <td>
              <p-autoComplete
                class="w-full"
                formControlName="manufacturerReference"
                #manufacturerSearch="cslServerSearch"
                [cslServerSearch]="fetchManufacturers"
                [suggestions]="manufacturerSearch.results()"
                (onSelect)="onManufacturerSelected(i, $event)"
                [placeholder]="'Search Manufacturer' | translateWF: 'Search Manufacturer'"
                [dropdown]="true"
                appendTo="body"
                field="name"
                styleClass="w-full"
                [ngClass]="{
                  'ng-invalid ng-dirty': row.get('manufacturerReference')?.invalid && row.get('manufacturerReference')?.touched,
                }"
              >
                <ng-template let-manufacturer pTemplate="item">
                  <div class="col">
                    <span class="font-semibold">{{ manufacturer.name }}</span>
                    @if (manufacturer.code) {
                      <span class="text-sm text-gray-600">Code: {{ manufacturer.code }}</span>
                    }
                  </div>
                </ng-template>
              </p-autoComplete>
            </td>
            <td>
              <p-autoComplete
                class="w-full"
                formControlName="manufacturerLotNumber"
                [suggestions]="getFilteredLotNumbers(i)"
                (completeMethod)="filterLotNumbers($event, i)"
                [placeholder]="'Enter Lot Number' | translateWF: 'Enter Lot Number'"
                dropdown="true"
                forceSelection="true"
                appendTo="body"
                styleClass="w-full"
                [ngClass]="{
                  'ng-invalid ng-dirty': row.get('manufacturerLotNumber')?.invalid && row.get('manufacturerLotNumber')?.touched,
                }"
              />
            </td>
            <td>
              @if (row.get('dateAdded')?.value) {
                <span class="text-sm">{{ row.get('dateAdded')?.value | date: 'dd/MM/yyyy' }}</span>
              } @else {
                <span class="text-sm text-gray-400">-</span>
              }
            </td>
            <td>
              @if (row.get('addedBy')?.value) {
                <span class="text-sm">{{ row.get('addedBy')?.value }}</span>
              } @else {
                <span class="text-sm text-gray-400">-</span>
              }
            </td>
            <td>
              @if (canEdit()) {
                <p-button icon="pi pi-trash" severity="danger" [text]="true" (onClick)="removeManufacturerLotNumber(i)" />
              }
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="5" class="text-center">No manufacturer lot numbers added. Click "Add" to add one.</td>
          </tr>
        </ng-template>
      </p-table>
    </section>
  </div>
</main>
